import type {
  FetchArgs,
  FetchBaseQueryMeta,
  TypedMutationOnQueryStarted,
} from '@reduxjs/toolkit/query'
import { getMappedLabels } from 'Modules/Communication/CommunicationSettings/shared/utils/labels-helpers'
import type { ILabel } from 'Modules/Communication/shared/models/Labels'
import {
  inbox2 as baseInbox2,
  EmailModelRead as BaseEmailModelRead,
  RecipientModelRead as BaseRecipientModelRead,
} from './baseApi/inbox2'
import type {
  AttachmentMetadataModel,
  LabelsModel,
  EmailListModelRead,
  Operation,
  PersonModel,
  EmailMessageQueryModel,
  PostEmailMessagesListApiResponse,
  SmsDetailedModelRead,
  SmsListModelRead,
  SmsMessageQueryModel,
  SmsMessageQueryResultRead,
  PutConfigurationsByClientIdSignatureApiArg,
  PutConfigurationsByClientIdSignatureApiResponse,
} from './baseApi/inbox2'
import type { baseQuery } from './baseQuery'
import type { OverrideResults } from './overrideResult'

export type * from './baseApi/inbox2'

export type RecipientType = 'To' | 'From' | 'Cc' | 'Bcc'

export type RecipientModelRead = Omit<
  BaseRecipientModelRead,
  'recipientType'
> & {
  recipientType?: RecipientType
}

export type EmailModelRead = Omit<
  BaseEmailModelRead,
  'recipients' | 'state' | 'status'
> & {
  recipients?: RecipientModelRead[] | null
  state?: MessageState
  status?: InboxMessageStatus
}

// The schema lists these enums as unnamed integers; the API sends their names.
export enum MessageState {
  Processed = 'Processed',
  Deleted = 'Deleted',
  PermanentlyDeleted = 'PermanentlyDeleted',
  Sent = 'Sent',
  Draft = 'Draft',
  Received = 'Received',
}

export enum InboxMessageStatus {
  Received = 'Received',
  Sent = 'Sent',
  Draft = 'Draft',
  Sending = 'Sending',
}

export enum MessageType {
  Mail = 'Mail',
  Sms = 'Sms',
}

export enum JunkMailDestination {
  Inbox = 'Inbox',
  Deleted = 'Deleted',
}

export type MoveJunkModel = {
  messageIds?: string[] | null
  destination?: JunkMailDestination
}

export type SmsDetailModel = Omit<
  SmsDetailedModelRead,
  'recipients' | 'state' | 'status' | 'type'
> & {
  recipients?: RecipientModelRead[] | null
  state?: MessageState
  status?: InboxMessageStatus
  type?: MessageType
}

// GET /email/{clientId}/junk/{messageId} returns JunkEmailModel, which the schema doesn't describe.
export type JunkEmailModel = {
  id?: string | null
  title?: string | null
  sentDate?: string | null
  hasAttachments?: boolean
  from?: PersonModel
  recipients?: RecipientModelRead[] | null
  contentHtml?: string | null
  queryId?: string | null
  attachmentsMetadata?: AttachmentMetadataModel[] | null
}

// GET /{email,sms}/messages/{messageId}/receipt returns ReceiptModel, which the schema doesn't describe.
export type ReceiptModel = {
  sendingStatus?: InboxMessageStatus
  recipients?: RecipientModelRead[] | null
}

// POST /{email,sms}/messages/{messageId}/send returns an empty Ok; the schema says string.
export type PostMessageSendResponse = null

// The API sends JsonPatchDocument as an operation array; the schema says object.
export type MessagesOperationsPatchModel = {
  model?: { id?: string | null; patchModel?: Operation[] }[] | null
}

export type SmsListModel = Omit<SmsListModelRead, 'state' | 'recipients'> & {
  state?: MessageState
  recipients?: RecipientModelRead[] | null
}

export type SmsListResult = Omit<SmsMessageQueryResultRead, 'result'> & {
  result?: SmsListModel[] | null
}

// The schema types queryStatus and sortColumn as integer enums, so the
// generated enums are `$0 … $5`, but the backend expects strings
// ('received', 'junk', 'all', …). Codegen's endpointOverrides can't change
// an argument type, so postEmailMessagesList and postSmsMessagesList are
// redefined below with these models. Drop both once the schema publishes
// string enums.
export type SmsQueryModel = Omit<
  SmsMessageQueryModel,
  'queryStatus' | 'sortColumn'
> & {
  queryStatus?: string
  sortColumn?: string
}

export type EmailQueryModel = Omit<
  EmailMessageQueryModel,
  'queryStatus' | 'sortColumn'
> & {
  queryStatus?: string
  sortColumn?: string
}

export type Office365SmtpConfigModel = {
  email?: string | null
  password?: string | null
  syncFromDate?: string | null
}

export type PostConfigurationsOffice365ByClientIdApiResponse = string
export type PostConfigurationsOffice365ByClientIdApiArg = {
  clientId?: string
  office365SmtpConfigModel: Office365SmtpConfigModel
}

export const isConfigurationResponse = (response: Response) =>
  response.ok || response.status === 404

export const toEmailConfiguration = <T>(
  response: T,
  meta: FetchBaseQueryMeta | undefined
): T | null =>
  meta?.response?.status === 204 || meta?.response?.status === 404
    ? null
    : response

type PatchOperation = { op?: string; path?: string | null; value?: unknown }

/**
 * The value when a JSON Patch only replaces the flag at `path` (lowercase),
 * else undefined. Only such a patch has a known effect on the cached list:
 * every other field (labels, draft content, …) needs the list refetch.
 */
export const flagOf = (
  operations: unknown,
  path: string
): boolean | undefined => {
  const ops = operations as PatchOperation[] | undefined
  if (!Array.isArray(ops) || ops.length === 0) return undefined
  const onlyFlag = ops.every(
    (op) => op.op === 'replace' && op.path?.toLowerCase() === path
  )
  return onlyFlag ? Boolean(ops[ops.length - 1].value) : undefined
}

/** flagOf for a bulk patch: one shared value, else undefined. */
export const bulkFlagOf = (
  patch: MessagesOperationsPatchModel | undefined,
  path: string
) => {
  const values = (patch?.model ?? []).map((entry) =>
    flagOf(entry.patchModel, path)
  )
  return values.length > 0 && values.every((value) => value === values[0])
    ? values[0]
    : undefined
}

type ListMove = 'processed' | 'unprocessed' | 'deleted'

// The backend's folder filters: Innboks and Sendt hold neither processed nor
// deleted mail, Behandlet no deleted mail, and deleting from Slettet hides it.
// Utkast and label lists ("all") may keep the email, so their refetch decides.
const LEFT_LISTS: Record<ListMove, string[]> = {
  processed: ['received', 'sent'],
  unprocessed: ['processed'],
  deleted: ['received', 'sent', 'processed', 'deleted'],
}

export const leavesList = (queryStatus: string, move: ListMove) =>
  LEFT_LISTS[move].includes(queryStatus)

type CacheApi = {
  dispatch: (action: unknown) => { undo: () => void }
  getState: () => unknown
}

type EmailList = PostEmailMessagesListApiResponse

const updateCachedLists = (
  { dispatch, getState }: CacheApi,
  recipe: (draft: EmailList, queryStatus: string) => void
) =>
  baseInbox2.util
    .selectInvalidatedBy(getState() as never, ['EmailMessages' as never])
    .filter(({ endpointName }) => endpointName === 'postEmailMessagesList')
    .map(({ originalArgs }) =>
      dispatch(
        baseInbox2.util.updateQueryData(
          'postEmailMessagesList',
          originalArgs,
          (draft) =>
            recipe(
              draft,
              String(originalArgs.emailMessageQueryModel?.queryStatus)
            )
        )
      )
    )

// Flips isRead on every cached list row for these ids, so a read-flag change
// needs no list refetch (the list refetch reloads every loaded page).
const setListRowsRead = (ids: string[], isRead: boolean, api: CacheApi) =>
  updateCachedLists(api, (draft) => {
    draft.result?.forEach((row) => {
      if (ids.includes(row.id)) row.isRead = isRead
    })
  })

// Drops the rows from the lists they are moving out of before the server
// answers, so the list can animate them away on click.
const removeListRows = (ids: string[], move: ListMove, api: CacheApi) =>
  updateCachedLists(api, (draft, queryStatus) => {
    if (!leavesList(queryStatus, move)) return
    draft.result = draft.result?.filter((row) => !ids.includes(row.id))
  })

const undoOnError = async (
  patches: { undo: () => void }[],
  queryFulfilled: Promise<unknown>
) => {
  try {
    await queryFulfilled
  } catch {
    patches.forEach((patch) => patch.undo())
  }
}

const bulkPatchRows = (
  ids: string[],
  patch: MessagesOperationsPatchModel,
  api: CacheApi
) => {
  const isRead = bulkFlagOf(patch, '/isread')
  if (isRead !== undefined) return setListRowsRead(ids, isRead, api)
  const isProcessed = bulkFlagOf(patch, '/isprocessed')
  if (isProcessed === undefined) return []
  return removeListRows(ids, isProcessed ? 'processed' : 'unprocessed', api)
}

const invalidatesConfiguration = (_result: unknown, error: unknown) =>
  error ? [] : ['EmailConfiguration' as const]

const invalidatesSms = (_result: unknown, error: unknown) =>
  error ? [] : ['SmsMessages' as const, 'SmsMessage' as const]

type QueryBuilder = { query?: (arg: never) => unknown }

const withContentTypeResponse = (definition: QueryBuilder) => {
  const { query } = definition
  definition.query = (arg: never) => ({
    ...(query(arg) as FetchArgs),
    responseHandler: 'content-type',
  })
}

const withContentTypeResponseInvalidatingConfiguration = (
  definition: QueryBuilder & { invalidatesTags?: unknown }
) => {
  withContentTypeResponse(definition)
  definition.invalidatesTags = invalidatesConfiguration
}

export const onPutConfigurationsByClientIdSignatureStarted: TypedMutationOnQueryStarted<
  PutConfigurationsByClientIdSignatureApiResponse,
  PutConfigurationsByClientIdSignatureApiArg,
  typeof baseQuery,
  'portal'
> = async ({ signatureUpsertModel }, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled
    dispatch(
      baseInbox2.util.updateQueryData(
        'getConfigurationsByClientIdSignature',
        {},
        (draft) => {
          draft.additionalLines = signatureUpsertModel.additionalLines
        }
      )
    )
  } catch {
    return
  }
}

const enhancedInbox2 = baseInbox2.enhanceEndpoints<
  | 'EmailMessages'
  | 'EmailMessage'
  | 'EmailStatistics'
  | 'EmailConfiguration'
  | 'JunkEmails'
  | 'SmsMessages'
  | 'SmsMessage',
  OverrideResults<
    typeof baseInbox2,
    {
      getEmailMessagesByMessageId: EmailModelRead
      getEmailMessagesByMessageIdRecipients: RecipientModelRead[]
      getSmsMessagesByMessageId: SmsDetailModel
      getSmsMessagesByMessageIdReceipt: ReceiptModel
      getEmailMessagesByMessageIdReceipt: ReceiptModel
      postSmsMessagesByMessageIdSend: PostMessageSendResponse
      postEmailMessagesByMessageIdSend: PostMessageSendResponse
      getLabels: ILabel[]
      postEmailByClientIdJunkList: EmailListModelRead[]
      getEmailByClientIdJunkAndMessageId: JunkEmailModel
    }
  >
>({
  addTagTypes: [
    'EmailMessages',
    'EmailMessage',
    'EmailStatistics',
    'EmailConfiguration',
    'JunkEmails',
    'SmsMessages',
    'SmsMessage',
  ],
  endpoints: {
    getConfigurationsByClientId: (definition) => {
      const { query } = definition
      definition.query = (queryArg) => ({
        ...(query(queryArg) as FetchArgs),
        responseHandler: 'content-type',
        validateStatus: isConfigurationResponse,
      })
      definition.transformResponse = (response, meta) =>
        toEmailConfiguration(response, meta as FetchBaseQueryMeta)
      definition.providesTags = ['EmailConfiguration']
    },
    postConfigurationsByClientId: withContentTypeResponse,
    postConfigurationsByClientIdSync: withContentTypeResponse,
    postConfigurationsByClientIdConfirmation:
      withContentTypeResponseInvalidatingConfiguration,
    deleteConfigurationsByClientId:
      withContentTypeResponseInvalidatingConfiguration,
    postConfigurationsGmailByClientId:
      withContentTypeResponseInvalidatingConfiguration,
    postEmailByClientIdJunkList: {
      providesTags: ['JunkEmails'],
    },
    getEmailByClientIdJunkCount: {
      providesTags: ['JunkEmails'],
    },
    getEmailMessagesStatistics: {
      providesTags: ['EmailStatistics'],
    },
    getEmailMessagesByMessageId: {
      providesTags: (_result, _error, arg) => [
        { type: 'EmailMessage', id: arg.messageId },
      ],
    },
    // Records this user's read receipt ("sett av") only: neither the list's
    // isRead flag nor the unread count changes, so nothing needs refetching.
    putEmailMessagesByMessageIdIsread: {
      invalidatesTags: [],
    },
    // A read-flag patch updates the cached rows in place; anything else can move
    // the email between folders, so the list (and the email) refetch.
    patchEmailMessagesByMessageId: {
      invalidatesTags: (_result, _error, arg) =>
        flagOf(arg.body, '/isread') === undefined
          ? [
              'EmailMessages',
              'EmailStatistics',
              { type: 'EmailMessage', id: arg.messageId },
            ]
          : ['EmailStatistics'],
      onQueryStarted: (arg, api) => {
        const isRead = flagOf(arg.body, '/isread')
        if (isRead === undefined) return
        return undoOnError(
          setListRowsRead([arg.messageId], isRead, api as never),
          api.queryFulfilled
        )
      },
    },
    deleteEmailMessages: {
      invalidatesTags: ['EmailMessages', 'EmailStatistics', 'EmailMessage'],
      onQueryStarted: (arg, api) =>
        undoOnError(
          removeListRows(
            arg.deleteMessagesRequestModel.messageIds ?? [],
            'deleted',
            api as never
          ),
          api.queryFulfilled
        ),
    },
    deleteEmailMessagesByMessageId: {
      invalidatesTags: ['EmailMessages', 'EmailStatistics'],
    },
    putEmailMessagesByMessageIdLabels: {
      invalidatesTags: (_result, _error, arg) => [
        'EmailMessages',
        { type: 'EmailMessage', id: arg.messageId },
      ],
    },
    putCasesByCaseIdMessages: {
      invalidatesTags: ['EmailMessages', 'EmailMessage'],
    },
    postEmailMessages: {
      invalidatesTags: ['EmailMessages', 'EmailStatistics'],
    },
    postEmailMessagesByMessageIdReply: {
      invalidatesTags: ['EmailMessages', 'EmailStatistics'],
    },
    postEmailMessagesByMessageIdForward: {
      invalidatesTags: ['EmailMessages', 'EmailStatistics'],
    },
    postEmailMessagesByMessageIdSend: {
      invalidatesTags: (_result, _error, arg) => [
        'EmailMessages',
        'EmailStatistics',
        { type: 'EmailMessage', id: arg.messageId },
      ],
    },
    patchEmailMessagesByMessageIdRecipients: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EmailMessage', id: arg.messageId },
      ],
    },
    // Refetching the message is how compose sees its new attachments.
    postEmailMessagesByMessageIdAttachments: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EmailMessage', id: arg.messageId },
      ],
    },
    deleteEmailMessagesByMessageIdAttachmentsAndAttachmentId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'EmailMessage', id: arg.messageId },
      ],
    },
    getSmsMessagesByMessageId: {
      providesTags: (_result, _error, arg) => [
        { type: 'SmsMessage', id: arg.messageId },
      ],
    },
    deleteSmsMessages: {
      invalidatesTags: invalidatesSms,
    },
    patchSmsMessagesByMessageId: {
      invalidatesTags: invalidatesSms,
    },
    deleteSmsMessagesByMessageId: {
      invalidatesTags: invalidatesSms,
    },
    getLabels: {
      transformResponse: (response: LabelsModel) =>
        getMappedLabels(response.labels ?? []),
    },
    putConfigurationsByClientIdSignature: {
      onQueryStarted: onPutConfigurationsByClientIdSignatureStarted,
    },
  },
})

export const inbox2Api = enhancedInbox2.injectEndpoints({
  endpoints: (build) => ({
    postConfigurationsOffice365ByClientId: build.mutation<
      PostConfigurationsOffice365ByClientIdApiResponse,
      PostConfigurationsOffice365ByClientIdApiArg
    >({
      query: (queryArg) => ({
        url: `/inbox2/Configurations/office365/${queryArg.clientId ?? ':clientId'}`,
        method: 'POST',
        body: queryArg.office365SmtpConfigModel,
        headers: {},
        responseHandler: 'content-type',
      }),
      invalidatesTags: invalidatesConfiguration,
    }),
    postEmailMessagesList: build.query<
      PostEmailMessagesListApiResponse,
      { emailMessageQueryModel: EmailQueryModel }
    >({
      query: (queryArg) => ({
        url: `/inbox2/email/messages/query`,
        method: 'POST',
        body: queryArg.emailMessageQueryModel,
      }),
      providesTags: ['EmailMessages'],
    }),
    postSmsMessagesList: build.query<
      SmsListResult,
      { smsMessageQueryModel: SmsQueryModel }
    >({
      query: (queryArg) => ({
        url: `/inbox2/sms/messages/query`,
        method: 'POST',
        body: queryArg.smsMessageQueryModel,
      }),
      providesTags: ['SmsMessages'],
    }),
    patchEmailMessages: build.mutation<
      unknown,
      { messagesPatchModel: MessagesOperationsPatchModel }
    >({
      query: (queryArg) => ({
        url: `/inbox2/email/messages`,
        method: 'PATCH',
        body: queryArg.messagesPatchModel,
      }),
      invalidatesTags: (_result, _error, arg) =>
        bulkFlagOf(arg.messagesPatchModel, '/isread') === undefined
          ? ['EmailMessages', 'EmailStatistics', 'EmailMessage']
          : ['EmailStatistics'],
      // A processed toggle also drops the rows right away; the refetch confirms.
      onQueryStarted: (arg, api) =>
        undoOnError(
          bulkPatchRows(
            (arg.messagesPatchModel.model ?? []).map((entry) => entry.id),
            arg.messagesPatchModel,
            api as never
          ),
          api.queryFulfilled
        ),
    }),
    putEmailByClientIdJunk: build.mutation<
      unknown,
      { clientId?: string; moveJunkModel: MoveJunkModel }
    >({
      query: (queryArg) => ({
        url: `/inbox2/email/${queryArg.clientId ?? ':clientId'}/junk`,
        method: 'PUT',
        body: queryArg.moveJunkModel,
      }),
      invalidatesTags: ['EmailMessages', 'EmailStatistics', 'JunkEmails'],
    }),
    patchSmsMessages: build.mutation<
      unknown,
      { messagesPatchModel: MessagesOperationsPatchModel }
    >({
      query: (queryArg) => ({
        url: `/inbox2/sms/messages`,
        method: 'PATCH',
        body: queryArg.messagesPatchModel,
      }),
      invalidatesTags: invalidatesSms,
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetConfigurationsByClientIdQuery,
  usePostConfigurationsByClientIdMutation,
  useDeleteConfigurationsByClientIdMutation,
  usePostConfigurationsGmailByClientIdMutation,
  usePostConfigurationsByClientIdConfirmationMutation,
  usePostConfigurationsByClientIdSyncMutation,
  usePostConfigurationsOffice365ByClientIdMutation,
  useGetEmailMessagesByMessageIdAttachmentsAndAttachmentIdQuery,
  useDeleteEmailMessagesByMessageIdAttachmentsAndAttachmentIdMutation,
  usePostEmailMessagesByMessageIdAttachmentsMutation,
  usePostEmailMessagesListQuery,
  useGetEmailMessagesStatisticsQuery,
  useGetEmailMessagesByMessageIdQuery,
  usePatchEmailMessagesByMessageIdMutation,
  useDeleteEmailMessagesByMessageIdMutation,
  usePutEmailMessagesByMessageIdLabelsMutation,
  useGetEmailMessagesByMessageIdLabelsQuery,
  usePatchEmailMessagesMutation,
  usePostEmailMessagesMutation,
  useDeleteEmailMessagesMutation,
  usePostEmailMessagesByMessageIdReplyMutation,
  usePostEmailMessagesByMessageIdForwardMutation,
  usePostEmailMessagesByMessageIdSendMutation,
  usePostEmailMessagesByMessageIdSendDirectMutation,
  useGetEmailMessagesByMessageIdRecipientsQuery,
  usePatchEmailMessagesByMessageIdRecipientsMutation,
  useGetEmailMessagesByMessageIdReceiptQuery,
  usePutEmailMessagesByMessageIdIsreadMutation,
  useGetEmailMessagesByMessageIdReadstatusSummaryQuery,
  useGetEmailMessagesByMessageIdReadstatusQuery,
  useGetLabelsQuery,
  usePostEmailByClientIdJunkListQuery,
  useGetEmailByClientIdJunkCountQuery,
  useGetEmailByClientIdJunkAndMessageIdQuery,
  usePutEmailByClientIdJunkMutation,
  useGetClientByClientIdAvailablecasesQuery,
  usePutCasesByCaseIdMessagesMutation,
  useGetFrequentrecipientsQuery,
  useGetConfigurationsByClientIdSignatureQuery,
  usePutConfigurationsByClientIdSignatureMutation,
  usePostSmsMessagesListQuery,
  useGetSmsMessagesByMessageIdQuery,
  usePatchSmsMessagesByMessageIdMutation,
  useDeleteSmsMessagesByMessageIdMutation,
  usePutSmsMessagesByMessageIdLabelsMutation,
  usePatchSmsMessagesMutation,
  usePostSmsMessagesMutation,
  useDeleteSmsMessagesMutation,
  usePostSmsMessagesByMessageIdSendMutation,
  useGetSmsMessagesByMessageIdRecipientsQuery,
  usePatchSmsMessagesByMessageIdRecipientsMutation,
  useGetSmsMessagesByMessageIdReceiptQuery,
  useGetSmsSettingsByClientIdQuery,
  useGetSmsStatusByClientIdQuery,
} = inbox2Api
